<script setup>
  // A deliberately non-dismissable gate for users whose terms acceptance is
  // missing or predates the latest policy revision. NOT built on AppModal.vue:
  // that component wires onClickOutside to close itself, which is exactly the
  // wrong behaviour here.
  //
  // This is a UX gate, not a security boundary - the API does not reject stale
  // acceptance, because apps/mobile and apps/crm share it and would be locked
  // out. The recorded timestamp is the compliance artifact, not this modal.
  import { computed, onMounted, ref } from 'vue'
  import { useAuthStore } from '@stores/auth.js'
  import { useApi } from '@/composables/useApi.js'
  import { PRIVACY_URL, TERMS_URL } from '@/helpers/legal.js'
  import AppButton from '@ui/AppButton.vue'

  const authStore = useAuthStore()
  const Api = useApi()

  const loading = ref(false)
  const updatedAt = ref('')

  const isOpen = computed(() => !!authStore.user?.terms_acceptance_required)

  const onAccept = async () => {
    loading.value = true
    // Deliberately not closed optimistically: on failure the modal stays put,
    // so a user never believes they accepted when nothing was recorded.
    await authStore.acceptTerms()
    loading.value = false
  }

  onMounted(async () => {
    // Presentational only. A failure here must not block acceptance, so the
    // date line simply stays hidden.
    try {
      const { data } = await Api('app-config/legal').get().json()
      const dates = [
        data.value?.data?.terms_updated_at,
        data.value?.data?.privacy_policy_updated_at
      ]
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()))

      if (dates.length) {
        const latest = new Date(Math.max(...dates.map((d) => d.getTime())))
        updatedAt.value = latest.toLocaleDateString()
      }
    } catch {
      updatedAt.value = ''
    }
  })
</script>

<template>
  <transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[10050] bg-black/70 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="bg-mobile-menu rounded-[16px] w-full max-w-[420px] p-6 text-center"
      >
        <h2 class="text-white text-lg mb-3">{{ $t('terms_updated_title') }}</h2>

        <p class="text-light-secondary text-sm mb-4">
          {{ $t('terms_updated_body') }}
        </p>

        <p
          v-if="updatedAt"
          class="text-light-secondary text-xs mb-4"
        >
          {{ $t('terms_updated_on', { date: updatedAt }) }}
        </p>

        <div class="flex justify-center gap-6 mb-6 text-sm">
          <a
            :href="TERMS_URL"
            target="_blank"
            rel="noopener"
            class="text-[#4497B0] underline"
            >{{ $t('terms') }}</a
          >
          <a
            :href="PRIVACY_URL"
            target="_blank"
            rel="noopener"
            class="text-[#4497B0] underline"
            >{{ $t('privacy') }}</a
          >
        </div>

        <app-button
          :text="$t('terms_accept_button')"
          :loading="loading"
          type="button"
          @click="onAccept"
        />

        <!-- Without a way out, a user who will not accept is stranded in an
             app they cannot use and cannot leave. -->
        <button
          type="button"
          class="mt-4 text-light-secondary text-xs underline"
          @click="authStore.logout()"
        >
          {{ $t('log_out') }}
        </button>
      </div>
    </div>
  </transition>
</template>

<style scoped lang="scss">
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
